import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Dialog,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
  Input,
} from "@salestrak/ui";

interface LinkPromptDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (url: string) => void;
}

/**
 * URL prompt for the editor's "Insert link" action — a sheet on a phone
 * (the responsive Dialog), with Insert on its title row.
 */
export function LinkPromptDialog({
  open,
  onOpenChange,
  onSubmit,
}: LinkPromptDialogProps) {
  const [value, setValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) setValue("");
  }, [open]);

  const submit = useCallback(() => {
    const trimmed = value.trim();
    if (trimmed) onSubmit(trimmed);
    onOpenChange(false);
  }, [value, onSubmit, onOpenChange]);

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      // Focus once the open settles — past Base UI's initial-focus handling,
      // and on a phone past the sheet's rise, so the keyboard never jumps it.
      onOpenChangeComplete={(isOpen) => {
        if (isOpen) inputRef.current?.focus();
      }}
    >
      <DialogPopup
        size="sm"
        dialogOptions={{
          primary: { label: "Insert", onClick: submit, disabled: !value.trim() },
        }}
      >
        <DialogHeader>
          <DialogTitle>Insert link</DialogTitle>
        </DialogHeader>
        <Box
          as="form"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <DialogPanel>
            <Input
              ref={inputRef}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="https://example.com"
              // inputMode (not type="url"): the URL keyboard without native
              // form validation — scheme-less links are legal here.
              inputMode="url"
              autoCapitalize="none"
              autoCorrect="off"
            />
          </DialogPanel>
        </Box>
      </DialogPopup>
    </Dialog>
  );
}
